import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api.js';
import { Layout } from '../components/Layout.js';
import { Icon } from '../components/ui/Icon.js';
import { GeneratePlanFlow } from '../components/GeneratePlanFlow.js';

interface MealPlanSummary {
  id: string;
  weekStart: string;
  versionHash: string;
  createdAt: string;
}

export function MealPlansPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [genError, setGenError] = useState<string | null>(null);
  const [showGenFlow, setShowGenFlow] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [busyWeek, setBusyWeek] = useState(false);

  const deletePlan = useMutation({
    mutationFn: (id: string) => api.delete(`/api/meal-plans/${id}`),
    onSuccess: () => {
      setConfirmDeleteId(null);
      qc.invalidateQueries({ queryKey: ['meal-plans'] });
    },
  });

  const { data: plans = [], isLoading } = useQuery<MealPlanSummary[]>({
    queryKey: ['meal-plans'],
    queryFn: () => api.get('/api/meal-plans'),
  });

  const { data: profile } = useQuery<{
    hiddenVeggieMode?: boolean;
    pickyEaterMode?: boolean;
    familyMembers?: { age: number }[] | null;
  }>({
    queryKey: ['profile'],
    queryFn: () => api.get('/api/profile'),
    staleTime: 5 * 60_000,
  });

  const blwActive = profile?.familyMembers?.some((m) => m.age === 0) ?? false;
  const hiddenVeggieMode = profile?.hiddenVeggieMode ?? false;
  const pickyEaterMode = profile?.pickyEaterMode ?? false;

  const generate = useMutation<{ planId: string }, unknown, { busyWeek: boolean }>({
    mutationFn: ({ busyWeek }) => api.post('/api/meal-plans/generate', {
      weekStart: getNextMonday(),
      preferences: null,
      busyWeek,
    }),
    onSuccess: () => {
      setGenError(null);
      qc.invalidateQueries({ queryKey: ['meal-plans'] });
    },
    onError: (err: any) => {
      setGenError(err?.body?.error ?? 'Generation failed. Please try again.');
    },
  });

  const startGenerate = () => {
    setGenError(null);
    setShowGenFlow(true);
    generate.reset();
    generate.mutate({ busyWeek });
  };

  const flowState: 'running' | 'success' | 'error' = generate.isPending
    ? 'running'
    : generate.isError
    ? 'error'
    : generate.isSuccess
    ? 'success'
    : 'running';

  return (
    <Layout>
      <div className="w-full mx-auto" style={{ maxWidth: 1280, padding: '32px 24px 56px' }}>

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="mb-8">
          <div className="text-xs font-semibold uppercase tracking-wider mb-2"
            style={{ color: 'var(--accent)', letterSpacing: '0.14em' }}>
            <Icon name="list" size={12} style={{ display: 'inline', marginRight: 6, marginBottom: -1 }} />
            Planning
          </div>
          <h1 className="h-display" style={{ fontSize: 'clamp(36px, 6vw, 56px)', margin: 0, color: 'var(--ink)', lineHeight: 1.0 }}>
            Weekly{' '}
            <span style={{ color: 'var(--accent)', fontStyle: 'italic', fontWeight: 400 }}>plans.</span>
          </h1>
          <p className="text-sm mt-3" style={{ color: 'var(--ink-3)' }}>
            {plans.length === 0
              ? "Generate your first plan and we'll line up the week's meals around your preferences."
              : <>
                  <strong style={{ color: 'var(--ink)' }}>{plans.length}</strong>{' '}
                  plan{plans.length === 1 ? '' : 's'} generated
                </>
            }
          </p>
        </div>

        <div className="flex items-center justify-end mb-6 flex-wrap gap-3">
          <label
            className="inline-flex items-center gap-2 cursor-pointer select-none"
            title="Force every meal to be ≤20 minutes for this week's plan"
          >
            <input
              type="checkbox"
              checked={busyWeek}
              onChange={(e) => setBusyWeek(e.target.checked)}
              className="sr-only"
              aria-label="Busy week — only quick meals"
            />
            <span
              className="w-9 h-5 rounded-full relative transition-colors"
              style={{
                background: busyWeek ? 'var(--accent)' : 'var(--line-soft)',
              }}
            >
              <span
                className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all"
                style={{ left: busyWeek ? 'calc(100% - 18px)' : '2px' }}
              />
            </span>
            <span className="text-sm" style={{ color: 'var(--ink-2)' }}>Busy week</span>
          </label>
          <button
            onClick={startGenerate}
            disabled={generate.isPending}
            className="text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
            style={{ background: 'var(--accent)' }}
          >
            Generate new plan
          </button>
        </div>

      {isLoading ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Loading…</p>
      ) : plans.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No meal plans yet. Generate your first one!</p>
      ) : (
        <div className="space-y-3">
          {plans.map((plan) => (
            <div key={plan.id} className="card px-5 py-4">
              {confirmDeleteId === plan.id ? (
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-sm text-gray-700 dark:text-gray-300">Delete this plan? This cannot be undone.</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => deletePlan.mutate(plan.id)}
                      disabled={deletePlan.isPending}
                      className="text-sm font-medium text-white bg-red-500 hover:bg-red-600 disabled:opacity-50 px-3 py-1.5 rounded-lg transition-colors"
                    >
                      {deletePlan.isPending ? 'Deleting…' : 'Yes, delete'}
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(null)}
                      className="text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                      Week of {new Date(plan.weekStart).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      Generated {new Date(plan.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Link to={`/meal-plans/${plan.id}`} className="text-sm font-medium hover:underline" style={{ color: 'var(--accent)' }}>
                      View →
                    </Link>
                    <button
                      onClick={() => setConfirmDeleteId(plan.id)}
                      className="text-sm text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                      aria-label="Delete plan"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showGenFlow && (
        <GeneratePlanFlow
          state={flowState}
          errorMessage={genError}
          blwActive={blwActive}
          hiddenVeggieMode={hiddenVeggieMode}
          pickyEaterMode={pickyEaterMode}
          onReview={() => {
            const id = generate.data?.planId;
            setShowGenFlow(false);
            if (id) navigate(`/meal-plans/${id}`);
          }}
          onReroll={() => {
            generate.reset();
            generate.mutate({ busyWeek });
          }}
          onClose={() => {
            setShowGenFlow(false);
            generate.reset();
          }}
        />
      )}
      </div>
    </Layout>
  );
}

function getNextMonday(): string {
  const d = new Date();
  const day = d.getDay();
  const diff = day === 1 ? 7 : (8 - day) % 7;
  d.setDate(d.getDate() + diff);
  return d.toISOString().split('T')[0];
}
